import { AuthAPI } from '@business/api/auth';
import { Endpoint } from '@business/api/endpoint';
import { AccountResponse, UserResponse, SignupRequest } from '@business/entities/account';
import { AppConfig } from '@core/utils/config';
import { R } from '@core/utils/r';
import { tapError } from '@core/utils/rxjs/operators';
import { Storage } from '@core/utils/storage';
import { BehaviorSubject, of, Subject, throwError } from 'rxjs';
import { finalize, switchMap, tap, catchError } from 'rxjs/operators';
import { openAccountSelectModal } from '../LoginPage/AccountSelectModal';
import { openTwoFactorVerifyModal } from '../LoginPage/TwoFactorModal';

export const KEY_TOKEN = 'auth:token';

class SessionManager {
  private readonly _token$ = new BehaviorSubject<string | null>(null);
  private readonly _user$ = new BehaviorSubject<UserResponse | null>(null);

  private readonly _sessionStart$ = new Subject<void | string>();
  private readonly _sessionEnd$ = new Subject<void>();
  private readonly _loggingOut$ = new Subject<boolean>();

  readonly sessionStart$ = this._sessionStart$.asObservable();
  readonly sessionEnd$ = this._sessionEnd$.asObservable();
  readonly loggingOut$ = this._loggingOut$.asObservable();

  readonly user$ = this._user$.asObservable();

  get token() {
    return this._token$.value;
  }

  set token(token: string | null) {
    this._token$.next(token);
  }

  get user() {
    return this._user$.value;
  }

  get isAuthenticated() {
    return !!this._token$.value;
  }

  patchUser(user: UserResponse) {
    this._user$.next(user);
  }

  loginWithTwoFactor(data: { username: string; password: string }, remember: boolean, redirectUrl?: string | null) {
    return AuthAPI.loginWithTwoFactor(data.username, data.password, remember).pipe(
      switchMap(result => {
        if (result.status == false) {
          return this.loginWithAccounts(data, remember, redirectUrl);
        } else {
          return openTwoFactorVerifyModal({ data: result }).pipe(
            switchMap(res => {
              if (res == 'VERIFIED') {
                return this.loginWithAccounts(data, remember, redirectUrl);
              } else {
                return of(null);
              }
            }),
          );
        }
      }),
    );
  }

  loginWithAccounts(data: { username: string; password: string }, remember: boolean, redirectUrl?: string | null) {
    return AuthAPI.loginForAccounts(data.username, data.password, remember).pipe(
      switchMap(result => {
        if (result.length > 1) {
          return openAccountSelectModal({ accounts: result as AccountResponse[] }).pipe(
            switchMap(appId => {
              if (R.isNil(appId)) {
                return of(null);
              }
              Endpoint.setAppId(appId);
              AppConfig.set({ appId });
              return this.login(data, remember, redirectUrl);
            }),
          );
        } else {
          const onlyAccount = R.first(result)!;
          Endpoint.setAppId(onlyAccount.appId);
          AppConfig.set({ appId: onlyAccount.appId, companyId: null });
          return this.login(data, remember, redirectUrl);
        }
      }),
    );
  }

  login(data: { username: string; password: string }, remember: boolean, redirectUrl?: string | null) {
    return AuthAPI.login(data.username, data.password, remember).pipe(
      switchMap(result => {
        const token = result.token;
        if (!token) throw new Error('Invalid token');

        return this.restoreByToken(token, remember, redirectUrl);
      }),
    );
  }

  signUp(data: SignupRequest) {
    return AuthAPI.signUp(data).pipe(
      switchMap(result => {
        const token = result.token;
        if (!token) throw new Error('Invalid token');
        return this.restoreByToken(token, true);
      }),
    );
  }

  logout(redirect = true) {
    return of(null).pipe(
      switchMap(() => {
        if (redirect) {
          this._loggingOut$.next(true);
        }

        return this._endSession(redirect);
      }),
      finalize(() => {
        if (redirect) {
          this._loggingOut$.next(false);
        }
      }),
    );
  }

  restore() {
    return of(null).pipe(
      switchMap(() => {
        const token = Storage.get<string>(KEY_TOKEN);
        if (!token) return throwError(new Error('No token'));

        return this.restoreByToken(token, true);
      }),
    );
  }

  reloadUser() {
    return of(null).pipe(
      switchMap(() => {
        const token = Storage.get<string>(KEY_TOKEN);
        if (!token) return throwError(new Error('No token'));

        return AuthAPI.check(token);
      }),
      tap(({ user }) => {
        this._user$.next(user);
        AppConfig.set({ companyId: AppConfig.current().companyId || user.companies[0].id });
      }),
    );
  }

  restoreByToken(token: string, remember: boolean, redirectUrl?: string | null) {
    return AuthAPI.check(token).pipe(
      switchMap(({ user }) => {
        if (remember) {
          Storage.set(KEY_TOKEN, token);
        }
        const config = AppConfig.current();
        const company = user.companies.find(company => company.id === config.companyId);
        const firstCompany = user.companies[0];
        if (!company && firstCompany) {
          AppConfig.set({ companyId: firstCompany.id });
        }

        return this._startSession(token, user, redirectUrl);
      }),
      tapError(error => {
        console.log('[Session.restoreByToken] error:', error);

        // TODO: show notification
      }),
    );
  }

  private _startSession(token: string, user: UserResponse, redirectUrl?: string | null) {
    return of(null).pipe(
      tap(() => {
        this._token$.next(token);
        this._user$.next(user);

        if (redirectUrl) {
          this._sessionStart$.next(redirectUrl);
        } else {
          this._sessionStart$.next();
        }
      }),
    );
  }

  private _endSession(redirect = true) {
    return of(null).pipe(
      tap(() => {
        this._token$.next(null);
        this._user$.next(null);

        Storage.remove(KEY_TOKEN);
        const appId = document.querySelector("meta[name='tenant']")!.getAttribute('content');
        AppConfig.set({ appId });
        Endpoint.setAppId(appId);

        if (redirect) {
          this._sessionEnd$.next();
        }
      }),
    );
  }
}

export const Session = new SessionManager();
